import { CheckBox, CheckBoxOutlined } from '@mui/icons-material';
import { Checkbox, FormControlLabel, SvgIcon, SvgIconProps, SxProps } from '@mui/material';
import { t } from 'i18next';
import { SEVERITY_COLORS, Severity, getLabel } from '../enums/Severity';

type Props = {
  severity: Severity;
  ariaLabelBase: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
  size?: 'small' | 'normal';
};

/**
 * Custom wrapper around checkbox for severities: necessary to
 * enforce custom color transformations to respect contrast guidelines
 */
export const SeverityCheckbox = ({ severity, ariaLabelBase, checked, disabled, onChange, size = 'normal' }: Props) => {
  const label = getLabel(severity);
  const ariaLabel = t(ariaLabelBase, { severity: label.toLocaleLowerCase() });

  const smallSx: SxProps = {
    '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
    '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
  };

  const colorSx: SxProps = {
    '& .MuiCheckbox-root.Mui-checked': {
      color: SEVERITY_COLORS[severity].main,
    },
  };

  // If we are rendering a checkbox for the LOW severity, we need to use an outlined icon
  // with a dark border, and fill the content with the LOW color.
  // Start from the `CheckboxOutlined` and provide a custom parent component which renders
  // the icon wrapper plus a nested `rect` element, sized to fit exactly the boundaries of
  // the outline: the `path` sx will make sure the outline is dark, and the `rect` will
  // inherit the default fill color of the icon
  const checkedIcon =
    severity === Severity.LOW ? (
      <CheckBoxOutlined
        sx={{ '& path': { fill: SEVERITY_COLORS[Severity.LOW].text } }}
        component={({ children, ...rest }: SvgIconProps) => (
          <SvgIcon {...rest}>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            {children}
          </SvgIcon>
        )}
      ></CheckBoxOutlined>
    ) : (
      <CheckBox />
    );

  return (
    <FormControlLabel
      disabled={disabled}
      sx={{ ...(size === 'small' ? smallSx : {}), ...colorSx }}
      control={
        <Checkbox
          checkedIcon={checkedIcon}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ title: ariaLabel, 'aria-label': ariaLabel }}
        />
      }
      label={label}
    />
  );
};
